import { FocusNode } from '@please/lrud'
import styled, { css } from 'styled-components'

import EventTileBg from '@/assets/bg/event-tile-bg.svg'
import {
  Label,
  TimeLabel,
  NonWatchableLabel,
  PremiumLabel,
  LabelIcon,
} from '@/components/EventHeading/styles'
import { LineTileShape, LineTileSize } from '@/constants/common'

import { getTileProps } from '../Lines/utils'

export const BadgesWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  & ${Label} {
    margin-left: 4px;
  }
`
export const TileLineElem = styled.div<{
  $tileShape: LineTileShape
  $tileSize: LineTileSize
}>`
  display: flex;
  width: ${({ $tileShape, $tileSize }) =>
    `${getTileProps($tileShape, $tileSize).w}px`};
  height: ${({ $tileShape, $tileSize }) =>
    `${getTileProps($tileShape, $tileSize).h}px`};
  background-image: url(\"${EventTileBg}\");
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 50%;
  position: relative;
  border-radius: ${({ $tileShape, $tileSize }) =>
    $tileShape === LineTileShape.CIRCLE
      ? '50%'
      : $tileSize === LineTileSize.SMALL || $tileSize === LineTileSize.XSMALL
      ? '12px'
      : '24px'};
  overflow: hidden;
  cursor: pointer;
  .name__item {
    ${({ $tileSize }) =>
      $tileSize !== LineTileSize.DEFAULT &&
      css`
        font-size: ${$tileSize === LineTileSize.LARGE ? '18px' : '14px'};
      `}
  }

  &.is_channel {
    justify-content: center;
    align-items: center;
    padding-bottom: 16px;
    &.isActiveChannel {
      background-color: ${(props) =>
        props.theme.colors.tiles.activeElem.background};
    }
  }
  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    &.channel_img {
      height: auto;
      width: auto;
      max-width: 85%;
      max-height: 50%;
    }
  }
  &:after,
  &.isFocusedLeaf,
  &.isFocusedLeaf::after {
    transition: transform 0.15s linear;
  }
  &:after {
    content: '';
    position: absolute;
    top: 0px;
    left: 0px;
    height: 100%;
    width: 100%;
    pointer-events: none;
    z-index: 10;
    box-shadow: inset 0px 0px 0px 4px transparent;
    border-radius: ${({ $tileShape, $tileSize }) =>
      $tileShape === LineTileShape.CIRCLE
        ? '50%'
        : $tileSize === LineTileSize.SMALL || $tileSize === LineTileSize.XSMALL
        ? '12px'
        : '24px'};
    overflow: hidden;
  }
  &.isFocusedLeaf {
    transform: ${({ $tileShape, $tileSize }) =>
      `scale(${getTileProps($tileShape, $tileSize).focusedTileScale})`};

    &::after {
      box-shadow: inset 0px 0px 0px 4px
        ${(props) => props.theme.colors.tiles.focusedElem.border};
    }
  }
  &.grid__item {
    height: 100%;
    width: 100%;
    &.isFocusedLeaf::after {
      margin-bottom: 4px;
    }
  }
  & ${BadgesWrapper} {
    ${({ $tileSize }) =>
      $tileSize === LineTileSize.SMALL &&
      css`
        & ${Label}:nth-child(n + 2) {
          display: none;
        }
        & ${Label} span {
          display: none;
        }
        & ${Label} {
          padding: 0;
          width: 24px;
          justify-content: center;
        }
        & ${LabelIcon} {
          margin: 0;
        }
      `}
  }
`
export const TileElem = styled(TileLineElem).attrs({
  as: FocusNode,
})``

export const TextShowMore = styled.div<{
  $tileShape: LineTileShape
  $tileSize: LineTileSize
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  font-weight: 600;
  font-size: ${({ $tileShape, $tileSize }) =>
    $tileShape !== LineTileShape.ASPECT16x9 && $tileSize === LineTileSize.XSMALL
      ? '14px'
      : '16px'};
  line-height: 1.4em;
  color: ${(props) => props.theme.colors.tiles.text};
  align-items: center;
  justify-content: center;
  height: 100%;
  text-transform: uppercase;
`
export const TilePreviewWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`

export const TileOversizeInfo = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: gold;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.25em;
  color: #065373;
  white-space: nowrap;
  padding: 0 0.25rem;
  > span {
    display: inline-block;
    padding: 0.25rem;
  }
`

export const TileTimeLabel = styled(TimeLabel)<{
  $isLiveNow: boolean
}>`
  position: absolute;
  top: 10px;
  left: 10px;
`

export const TilePremiumLabel = styled(PremiumLabel)``

export const TilePurchasedLabel = styled(TilePremiumLabel)``
export const TileNonWatchableLabel = styled(NonWatchableLabel)``

export const TileNameElem = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.25em;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.text};
  overflow: hidden;
`

export const NameText = styled.div<{
  $isMultiline?: boolean
}>`
  overflow: hidden;
  text-align: left;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

export const TileDescriptionElem = styled.div`
  padding-top: 4px;
  width: 100%;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.2em;
  color: ${(props) => props.theme.colors.tiles.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
`
export const TilePlay = styled.img`
  &.play__item {
    position: absolute;
    height: 150px;
    width: 120px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`
export const LockIcon = styled.img`
  &.lock__item {
    position: absolute;
    height: 50px;
    width: 50px;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 2px solid #fff;
    overflow: hidden;
  }
`
export const TileLogoWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 4px;
  right: 0px;
  width: 100px;
  height: calc(100% - 6px);
`
export const TileLogo = styled.img`
  max-width: 76%;
  max-height: 60%;
  object-fit: contain;
`

export const TileDescriptionWrapper = styled.div<{
  $hasTileShading?: boolean
  $fullWidth?: boolean
  $tileSize?: LineTileSize
  $isPageTileWithTitle?: boolean
}>`
  position: absolute;
  width: 100%;
  bottom: 0;
  pointer-events: none;
  height: ${({ $tileSize }) =>
    $tileSize === LineTileSize.SMALL ? `48px` : '64px'};
  padding: 0px 16px;
  padding-right: ${({ $fullWidth, $tileSize }) =>
    $fullWidth ? `10px` : $tileSize === LineTileSize.SMALL ? '80px' : '100px'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: ${({ theme, $hasTileShading }) =>
    $hasTileShading ? `rgba(${theme.colors.tiles.shading}, 0.75)` : 'none'};

  ${TileLogoWrapper} {
    width: ${({ $tileSize }) =>
      $tileSize === LineTileSize.SMALL ? `80px` : '100px'};
  }
  ${TileDescriptionElem} {
    font-size: ${({ $tileSize }) =>
      $tileSize === LineTileSize.SMALL ? `16px` : '18px'};
  }

  ${({ $isPageTileWithTitle, $hasTileShading, $tileSize }) =>
    $isPageTileWithTitle &&
    css`
      height: 100%;
      background: ${({ theme }) =>
        $hasTileShading ? `rgba(${theme.colors.tiles.shading}, 0.5)` : 'none'};
      ${TileNameElem} {
        justify-content: center;
        text-align: center;
        font-size: ${$tileSize === LineTileSize.LARGE
          ? '28px'
          : $tileSize === LineTileSize.SMALL
          ? '16px'
          : '24px'};
        color: ${(props) => props.theme.colors.tiles.text};
      }
    `}
`

export const TileProgressElem = styled.div<{
  $isBottomPosition?: boolean
}>`
  display: block;
  height: 4px;
  width: 100%;
  position: absolute;
  top: ${({ $isBottomPosition }) =>
    $isBottomPosition ? `calc(100% - 4px)` : '-4px'};
  left: 0;
  background: ${(props) => props.theme.colors.tiles.progressBg};
  z-index: 1;
`
interface ProgressValueProps {
  $viewed: number
}

export const TileProgressValue = styled.div.attrs<ProgressValueProps>(
  (props) => ({
    style: { width: `${props.$viewed}%` },
  }),
)<ProgressValueProps>`
  display: block;
  height: 4px;
  position: absolute;
  top: 0;
  left: 0;
  background: ${(props) => props.theme.colors.tiles.progressLine};
  max-width: 100%;
`

export const EmptyTileElem = styled.div`
  position: relative;
  display: flex;
  width: ${getTileProps().w + 'px'};
  height: ${getTileProps().h + 'px'};
  box-shadow: 0 0 0 1px ${(props) => props.theme.colors.squareTile.background};
  margin: 4px;
  font-size: 18px;
  padding: 25px;
  line-height: 1.5;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.squareTile.background};
  border-radius: 24px;
  overflow: hidden;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    box-shadow: inset 0 0 0 4px
      ${(props) => props.theme.colors.emptyTile.focusedElem.border};
    border-radius: 24px;
    overflow: hidden;
    transition: all 0.3s linear;
  }
`

export const EmptyPlayerElem = styled(EmptyTileElem)``
export const HtmlWidgetLineTileElem = styled(TileLineElem)<{
  $isGridTile: boolean
}>`
  position: relative;
  & > img {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
`
export const HtmlWidgetTileElem = styled(HtmlWidgetLineTileElem).attrs({
  as: FocusNode,
})``

export const TileDebugInfo = styled.div`
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  color: ${(props) => props.theme.colors.accent};
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 0.5em;
  background-color: black;
`
